// RecipientBar — the modal's `.ra-modal__recip-bar`: the "Recipient:" label,
// selected recipient chips, the "+" add button, and the picker popover it
// toggles. Pure presentation over a RecipientSelection.

import { Box, Flex, Icon } from "@chakra-ui/react";
import { TbPlus } from "react-icons/tb";
import RecipientChip from "components/ds/RecipientChip";
import RecipientPicker from "./RecipientPicker";
import type { RecipientSelection } from "./useRecipientSelection";

const RecipientBar = ({ selection }: { selection: RecipientSelection }) => {
  const { recipients, pickerPeople, selectedIds, pickerOpen, setPickerOpen } =
    selection;

  return (
    <Flex
      align="center"
      gap="14px"
      px="28px"
      py="lg"
      borderBottomWidth="1px"
      borderBottomStyle="solid"
      borderBottomColor="neutral.100"
      flexWrap="wrap"
      position="relative"
      h="72px"
    >
      <Box fontSize="md" fontWeight={600} color="neutral.950">
        Recipient:
      </Box>
      <Flex align="center" gap="sm" flexWrap="wrap">
        {recipients.map((r) => (
          <RecipientChip
            key={r.id}
            name={r.name}
            suffix={r.suffix}
            avatarSrc={r.avatarSrc}
            tone={r.tone}
            isGroup={r.isGroup}
            groupCount={r.groupCount}
            // Clicking the chip opens the same recipient picker as the "+".
            onClick={() => setPickerOpen(!pickerOpen)}
            // Don't allow removing the only recipient — submission requires
            // at least one.
            onRemove={
              recipients.length > 1 ? () => selection.remove(r.id) : undefined
            }
          />
        ))}
        {/* .ra-recip-add — 32×32 outlined "+" toggling the picker popover. */}
        <Box
          as="button"
          type="button"
          aria-label="Add recipient"
          onClick={() => setPickerOpen(!pickerOpen)}
          w="32px"
          h="32px"
          borderRadius="component.lg"
          borderWidth="1px"
          borderStyle="solid"
          borderColor="neutral.300"
          bg="neutral.0"
          color="neutral.800"
          display="inline-flex"
          alignItems="center"
          justifyContent="center"
          cursor="pointer"
          transition="border-color 120ms cubic-bezier(0.23, 1, 0.32, 1), background-color 120ms cubic-bezier(0.23, 1, 0.32, 1), color 120ms cubic-bezier(0.23, 1, 0.32, 1), transform 100ms cubic-bezier(0.23, 1, 0.32, 1)"
          _hover={{
            borderColor: "neutral.400",
            bg: "neutral.50",
            color: "neutral.950",
          }}
          _active={{ transform: "scale(0.96)" }}
        >
          <Icon as={TbPlus} boxSize="16px" />
        </Box>
      </Flex>
      {pickerOpen && (
        <RecipientPicker
          people={pickerPeople}
          selectedIds={selectedIds}
          onToggle={selection.toggle}
          onClose={() => setPickerOpen(false)}
        />
      )}
    </Flex>
  );
};

export default RecipientBar;
